import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { cacheData, getCachedData } from "@/lib/offlineStore";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

// Re-export the central utility function for backwards compatibility.
// Only getProfileDisplayName is re-exported here — it has live consumers that import
// it from this hook (AssignableUserOptions, AssignBulkDialog, DefectDetailContent, Defects).
// getProfileInitials and the DisplayableProfile type are consumed directly from
// "@/lib/profileUtils", so re-exporting them from here was dead (TES-1614 cleanup).
export { getProfileDisplayName } from "@/lib/profileUtils";

export interface AssignableProfile {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  is_provisioned?: boolean;
}

// Query keys for cache management
export const assignableProfilesKeys = {
  all: ["assignableProfiles"] as const,
  defects: ["assignableProfiles", "defects"] as const,
  inspections: ["assignableProfiles", "inspections"] as const,
};

const CACHE_KEY_DEFECTS = "assignable-profiles-defects";
const CACHE_KEY_INSPECTIONS = "assignable-profiles-inspections";

export async function fetchWithOfflineFallback(
  rpcName: "list_assignable_profiles" | "list_inspection_assignable_profiles",
  cacheKey: string,
  isOnline: boolean,
  offlineEnabled: boolean,
): Promise<AssignableProfile[]> {
  // Offline path: return cached data
  if (!isOnline && offlineEnabled) {
    const cached = await getCachedData<AssignableProfile[]>(cacheKey);
    if (cached?.data) return cached.data;
    return [];
  }

  // Online path: fetch and cache
  const { data, error } = await supabase.rpc(rpcName);
  if (error) throw error;
  const profiles = (data || []) as AssignableProfile[];

  // Cache in background if offline mode is enabled
  if (offlineEnabled) {
    cacheData(cacheKey, profiles).catch(() => {});
  }

  return profiles;
}

/**
 * Fetches users assignable to defects (excludes admins + disabled users)
 */
export function useAssignableProfilesForDefects(teamId?: string | null, enabled = true) {
  const { isOnline } = useOnlineStatus();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Push-based: invalidate when profiles or roles change
  useRealtimeSubscription(
    ["profiles", "user_roles"],
    () => queryClient.invalidateQueries({ queryKey: assignableProfilesKeys.defects }),
    { enabled: !!user && enabled },
  );

  return useQuery({
    queryKey: [...assignableProfilesKeys.defects, teamId ?? "all"],
    queryFn: async () => {
      if (teamId) {
        // Use team-filtered RPC
        if (!isOnline && offlineEnabled) {
          const cached = await getCachedData<AssignableProfile[]>(`${CACHE_KEY_DEFECTS}-${teamId}`);
          if (cached?.data) return cached.data;
          return [];
        }
        const { data, error } = await supabase.rpc("list_team_assignable_profiles", {
          _team_id: teamId,
        });
        if (error) throw error;
        const profiles = (data || []) as AssignableProfile[];
        if (offlineEnabled) {
          cacheData(`${CACHE_KEY_DEFECTS}-${teamId}`, profiles).catch(() => {});
        }
        return profiles;
      }
      return fetchWithOfflineFallback(
        "list_assignable_profiles",
        CACHE_KEY_DEFECTS,
        isOnline,
        offlineEnabled,
      );
    },
    enabled: !!user && enabled,
    staleTime: isOnline ? undefined : Infinity,
    gcTime: isOnline ? undefined : Infinity,
    retry: isOnline ? 3 : false,
  });
}

/**
 * Fetches users assignable to inspections (excludes admins + mangelbearbeiter + disabled users)
 */
export function useAssignableProfilesForInspections(teamId?: string | null, enabled = true) {
  const { isOnline } = useOnlineStatus();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Push-based: invalidate when profiles or roles change
  useRealtimeSubscription(
    ["profiles", "user_roles"],
    () => queryClient.invalidateQueries({ queryKey: assignableProfilesKeys.inspections }),
    { enabled: !!user && enabled },
  );

  return useQuery({
    queryKey: [...assignableProfilesKeys.inspections, teamId ?? "all"],
    queryFn: async () => {
      if (teamId) {
        // Use team-filtered RPC
        if (!isOnline && offlineEnabled) {
          const cached = await getCachedData<AssignableProfile[]>(
            `${CACHE_KEY_INSPECTIONS}-${teamId}`,
          );
          if (cached?.data) return cached.data;
          return [];
        }
        const { data, error } = await supabase.rpc("list_team_inspection_assignable_profiles", {
          _team_id: teamId,
        });
        if (error) throw error;
        const profiles = (data || []) as AssignableProfile[];
        if (offlineEnabled) {
          cacheData(`${CACHE_KEY_INSPECTIONS}-${teamId}`, profiles).catch(() => {});
        }
        return profiles;
      }
      return fetchWithOfflineFallback(
        "list_inspection_assignable_profiles",
        CACHE_KEY_INSPECTIONS,
        isOnline,
        offlineEnabled,
      );
    },
    enabled: !!user && enabled,
    staleTime: isOnline ? undefined : Infinity,
    gcTime: isOnline ? undefined : Infinity,
    retry: isOnline ? 3 : false,
  });
}

/**
 * Hook to invalidate all assignable profiles queries.
 * Call this after enabling/disabling a user.
 */
export function useInvalidateAssignableProfiles() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: assignableProfilesKeys.all });
  };
}
